import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import {
  deleteSubmissionFromApi,
  getInitials,
  getSubmissionReviewLabel,
  getSubmissionSourceLabel,
  isBmsMemberSource,
  loadSubmissionsFromApi,
  SUBMISSION_REVIEW,
  SUBMISSION_REVIEW_OPTIONS,
} from "./submissionStorage";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.5,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.4, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.35, fontSize: 11, color: MUTED }}>{hint}</Typography>
    ) : null}
  </Box>
);

const reviewColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

export default function TrackerDetailSubmissionsTab({ tracker }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);

  const trackerId = tracker?.id || tracker?._id;

  const reload = async () => {
    if (!trackerId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await loadSubmissionsFromApi(trackerId, { limit: 500 });
    setRows(result.rows || []);
    setLoading(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Failed to load submissions", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    reload();
  }, [trackerId]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.statusName) set.add(r.statusName);
    });
    return Array.from(set).sort();
  }, [rows]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    (Array.isArray(tracker?.tags) ? tracker.tags : []).forEach((t) => {
      if (t) set.add(String(t));
    });
    rows.forEach((r) => {
      (Array.isArray(r.tags) ? r.tags : []).forEach((t) => {
        if (t) set.add(String(t));
      });
    });
    return Array.from(set).sort();
  }, [rows, tracker?.tags]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (reviewFilter !== "all" && row.reviewStatus !== reviewFilter) return false;
      if (statusFilter !== "all" && row.statusName !== statusFilter) return false;
      if (tagFilter !== "all") {
        const tags = Array.isArray(row.tags) ? row.tags : [];
        if (!tags.some((t) => String(t) === tagFilter)) return false;
      }
      if (!q) return true;
      const hay = `${row.memberName || ""} ${row.email || ""} ${row.formName || ""} ${
        row.coachName || ""
      } ${row.statusName || ""} ${(row.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, reviewFilter, statusFilter, tagFilter]);

  const stats = useMemo(() => {
    const people = new Set();
    const byPerson = {};
    filtered.forEach((row) => {
      const key = row.memberId || row.email || row.memberName || row.id;
      if (key) {
        people.add(key);
        byPerson[key] = (byPerson[key] || 0) + 1;
      }
    });
    return {
      shown: filtered.length,
      people: people.size,
      multi: Object.values(byPerson).filter((n) => n > 1).length,
      reviewsDue: filtered.filter(
        (r) =>
          r.reviewStatus === SUBMISSION_REVIEW.PENDING ||
          r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
      ).length,
    };
  }, [filtered]);

  const handleDelete = async () => {
    if (!deleteDoc?.id) return;
    const result = await deleteSubmissionFromApi(deleteDoc.id);
    setOpenDelete(false);
    setDeleteDoc(null);
    if (result.ok) {
      enqueueSnackbar("Submission deleted", { variant: "success" });
      reload();
    } else {
      enqueueSnackbar(result.message || "Delete failed", { variant: "error" });
    }
  };

  const exportCsv = () => {
    const lines = [
      [
        "Person",
        "Email",
        "Form",
        "Status",
        "Tags",
        "Review",
        "Coach",
        "Source",
        "Submitted",
      ].join(","),
      ...filtered.map((row) =>
        [
          row.memberName,
          row.email,
          row.formName,
          row.statusName,
          (row.tags || []).join("; "),
          getSubmissionReviewLabel(row.reviewStatus),
          row.coachName,
          getSubmissionSourceLabel(row.source),
          row.submittedAt
            ? moment(row.submittedAt).format("YYYY-MM-DD HH:mm")
            : "",
        ]
          .map((v) => `"${String(v || "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(tracker?.name || "tracker").replace(/\s+/g, "_")}_submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const MENU_OPTIONS = [
    {
      label: "View",
      icon: "akar-icons:eye",
      handleClick: (row) => navigate(`/tracker-submissions/${row.id}`),
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    { id: "person", label: "Person" },
    { id: "form", label: "Form" },
    { id: "status", label: "Status" },
    { id: "tags", label: "Tags" },
    { id: "review", label: "Review" },
    { id: "coach", label: "Coach" },
    { id: "submitted", label: "Submitted" },
  ];

  const tableRows = filtered.map((row) => ({
    ...row,
    person: (
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: isBmsMemberSource(row.source) ? "#8b5cf6" : "#04abbb",
          }}
        >
          {getInitials(row.memberName)}
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {row.memberName || "—"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED }}>
            {getSubmissionSourceLabel(row.source)}
            {row.isNew ? " · New" : ""}
          </Typography>
        </Box>
      </Stack>
    ),
    form: (
      <Typography sx={{ fontSize: 13 }}>{row.formName || "Form"}</Typography>
    ),
    status: (
      <Chipish color={row.statusColor} label={row.statusName || "—"} />
    ),
    tags: (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {(row.tags || []).length
          ? (row.tags || []).map((tag) => (
              <Chipish key={tag} label={tag} />
            ))
          : (
              <Typography sx={{ fontSize: 13, color: MUTED }}>—</Typography>
            )}
      </Box>
    ),
    review: (
      <Label variant="ghost" color={reviewColor(row.reviewStatus)}>
        {getSubmissionReviewLabel(row.reviewStatus)}
      </Label>
    ),
    coach: (
      <Typography sx={{ fontSize: 13 }}>{row.coachName || "—"}</Typography>
    ),
    submitted: (
      <Typography sx={{ fontSize: 13 }}>
        {row.submittedAt
          ? moment(row.submittedAt).format("DD MMM YYYY HH:mm")
          : "—"}
      </Typography>
    ),
    MENU_OPTIONS,
  }));

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this submission?"
        handleAgree={handleDelete}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            Submissions
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Every form entry on this journey — one row per submit.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", sm: 220 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={exportCsv}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Export CSV
          </button>
        </Stack>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        <StatCard label="Shown" value={stats.shown} hint="Matching filters" />
        <StatCard label="People" value={stats.people} hint="Unique" />
        <StatCard label="Repeat" value={stats.multi} hint="More than once" />
        <StatCard label="Reviews due" value={stats.reviewsDue} hint="Pending + overdue" />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          select
          size="small"
          label="Review"
          value={reviewFilter}
          onChange={(e) => setReviewFilter(e.target.value)}
          sx={fieldSx}
        >
          {SUBMISSION_REVIEW_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={fieldSx}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {statusOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Tag"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          sx={fieldSx}
        >
          <MenuItem value="all">All tags</MenuItem>
          {tagOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <CustomMUITable
          TABLE_HEAD={TABLE_HEAD}
          data={tableRows}
          MENU_OPTIONS={MENU_OPTIONS}
          hide_search
          className="card-with-background tracker-module-table"
        />
      )}
    </>
  );
}

function Chipish({ color, label }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        px: 0.85,
        py: 0.2,
        borderRadius: "8px",
        fontSize: 11.5,
        fontWeight: 700,
        bgcolor: color ? `${color}22` : PRIMARY_SOFT,
        color: color || PRIMARY,
      }}
    >
      {label}
    </Box>
  );
}
